import React, { useEffect, useState } from "react";
import { BrandLogo } from "@/components/shared";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, ListChecks, BadgeCheck, Disc3,
  UserPlus, LifeBuoy, Settings2, ShieldCheck, LogOut, Menu, X,
  ArrowDownToLine, ArrowUpFromLine, Megaphone, CreditCard, Plane, CupSoda, Package,
} from "lucide-react";
import LangToggle from "@/components/LangToggle";
import LiveClock from "@/components/LiveClock";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

const items = [
  ["/admin", "admin_dashboard", LayoutDashboard],
  ["/admin/members", "members", Users],
  ["/admin/deposits", "deposit_mgmt", ArrowDownToLine],
  ["/admin/withdrawals", "withdrawal_mgmt", ArrowUpFromLine],
  ["/admin/events", "events", CalendarDays],
  ["/admin/predictions", "all_predictions", ListChecks],
  ["/admin/kyc", "kyc", BadgeCheck],
  ["/admin/spin", "spin_control", Disc3],
  ["/admin/aviator", "aviator_mgmt", Plane],
  ["/admin/three-cups", "three_cups_mgmt", CupSoda],
  ["/admin/deal-or-no-deal", "dnd_mgmt", Package],
  ["/admin/referrals", "referrals", UserPlus],
  ["/admin/ads", "ads_mgmt", Megaphone],
  ["/admin/payments", "payment_settings", CreditCard],
  ["/admin/support", "support", LifeBuoy],
  ["/admin/misc", "settings", Settings2],
];

export default function AdminLayout() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="ec-bg min-h-screen flex">
      <aside className={`fixed z-40 inset-y-0 start-0 w-64 glass border-e border-white/10 flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"}`}>
        <div className="h-16 flex items-center gap-2 px-5 font-head font-extrabold border-b border-white/10">
          <BrandLogo size="w-10 h-10" text="EC DREAM" textClass="text-base" testid="admin-brand-logo" /><span className="text-[10px] uppercase tracking-widest text-yellow-600 font-bold ms-1">Admin</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map(([to, k, Icon]) => (
            <NavLink key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${k}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-orange-500/15 text-orange-400 border border-orange-500/30" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <Icon className="w-5 h-5" /> {t(k)}
            </NavLink>
          ))}
          <button onClick={logout} data-testid="admin-logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 mt-4"><LogOut className="w-5 h-5" /> {t("logout")}</button>
        </nav>
      </aside>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 lg:ms-64 min-w-0">
        <header className="sticky top-0 z-20 glass h-16 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="admin-menu-toggle">{open ? <X /> : <Menu />}</button>
            <LiveClock className="hidden sm:inline-flex" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 hidden sm:inline">{user?.name}</span>
            <LangToggle compact />
            <div className="w-9 h-9 rounded-full bg-orange-500/20 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-orange-400" /></div>
          </div>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
